import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import HeaderCard from "../../components/RetailerBusinessAgent/HeaderCard";
import ReferralLinkCard from "../../components/RetailerBusinessAgent/ReferralLinkCard";
import StatusGrid from "../../components/RetailerBusinessAgent/StatusGrid";
import { API_URL } from "../../constant";
import logo from "../../images/site_logo/TC-Logo-icon.png";
import dollarY from "../../images/shapes/Dollar Bag (1).png";
import business from "../../images/shapes/Business.png";
import building from "../../images/shapes/Business Building.png";

const ReferCustomer = () => {
  const navigate = useNavigate();
  const { userData } = useOutletContext();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [agentWalletData, setAgentWalletData] = useState(null);
  const [containerStyle, setContainerStyle] = useState({});

  useEffect(() => {
    if (!userData) {
      navigate("/sign_in");
      return;
    }

    // Set background based on role
    setContainerStyle(getContainerStyle(userData?.role));

    // Only advertising agents can access this page
    if (!userData.isAdvertisingAgent) {
      navigate("/customer/wallet");
      return;
    }

    fetchReferralData();
    fetchWalletData();
  }, [userData, navigate]);

  const getContainerStyle = (role) => {
    let background = "";

    switch (role) {
      case "Senior Customer Advertising Agent":
        background = "linear-gradient(to bottom, #0d6efd, white)";
        break;
      case "Mini Customer Advertising Agent":
        background = "linear-gradient(to bottom, #0000a0, white)";
        break;
      case "Customer":
      default:
        background = "linear-gradient(to bottom, #6f42c1, white)";
        break;
    }

    return {
      display: "flex",
      flexDirection: "column",
      marginTop: "0px",
      marginBottom: "0px",
      background: background,
      width: "100%",
      minHeight: "100vh",
      fontFamily: "'Poppins', sans-serif",
      padding: "20px",
    };
  };

  const fetchWalletData = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/sign_in");
      return;
    }

    try {
      // Get main wallet data
      const walletResponse = await fetch(`${API_URL}/api/wallet`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (walletResponse.ok) {
        const walletData = await walletResponse.json();
        setWalletData(walletData);
      }

      // Get advertising agent wallet data if user is an agent
      if (userData?.isAdvertisingAgent) {
        try {
          const agentResponse = await fetch(
            `${API_URL}/api/advertising-agents/wallet`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (agentResponse.ok) {
            const agentWalletResponse = await agentResponse.json();
            setAgentWalletData(agentWalletResponse.data || null);
          }
        } catch (error) {
          console.error("Error fetching agent wallet:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
    }
  };

  const fetchReferralData = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/sign_in");
      return;
    }

    setIsLoading(true);
    try {
      // Get referral information
      const referralResponse = await fetch(
        `${API_URL}/api/customer/referral/info`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!referralResponse.ok)
        throw new Error("Failed to fetch referral data");
      const referralInfo = await referralResponse.json();

      // Get referral stats
      const statsResponse = await fetch(
        `${API_URL}/api/customer/referral/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      let stats = { total_referrals: 0, referrals: [], pending_earnings: 0 };
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        // Map API response to match expected structure
        stats = {
          ...statsData,
          total_referred: statsData.total_referrals || 0, // Map total_referrals to total_referred
          pending_earnings: statsData.pending_earnings || 0,
        };
      }

      // Transform data to match StatusGrid expectations
      const transformedData = {
        user: {
          ...userData,
          referralLink:
            referralInfo.referral_url ||
            `${window.location.origin}/sign_in?ref=${referralInfo.referral_code}`,
        },
        counts: {
          customersReferred: stats.total_referred || 0,
        },
        earnings: {
          topcoinGBP: (walletData?.points || 0) * 0.005,
          earnedBonus: stats.pending_earnings || 0,
          agentEarnings: (agentWalletData?.points || 0) * 0.005,
          cashBalance: walletData?.cash_balance || 0,
        },
        dashboardData: {
          ...referralInfo,
          ...stats,
        },
      };

      setReferralData(transformedData);
      setStatsData(stats);
    } catch (error) {
      console.error("Error fetching referral data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (walletData && agentWalletData !== undefined) {
      // Update referralData when wallet data is available
      if (referralData) {
        const updatedReferralData = {
          ...referralData,
          earnings: {
            ...referralData.earnings,
            topcoinGBP: (walletData?.points || 0) * 0.005,
            agentEarnings: (agentWalletData?.points || 0) * 0.005,
            cashBalance: walletData?.cash_balance || 0,
          },
        };
        setReferralData(updatedReferralData);
      }
    }
  }, [walletData, agentWalletData]);

  const handleCopyLink = async () => {
    if (referralData?.user?.referralLink) {
      try {
        await navigator.clipboard.writeText(referralData.user.referralLink);
        alert("Referral link copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    }
  };

  const handleShareLink = () => {
    if (!referralData?.user?.referralLink) {
      alert("Referral link not available");
      return;
    }

    const referralLink = referralData.user.referralLink;

    // Formatted share message for customers
    const shareMessage = `Topcoin UK is the Next Generation Loyalty Platform.

Discover the UK's Smartest Rewards Evolution.

I'm pleased to share that I've recently joined TopCoin UK, the UK's smartest and most advanced rewards evolution. With TopCoin UK, customers earn free TopCoin every time they shop at participating businesses across the country and worldwide.

If you're interested in joining as a customer and enjoying the benefits, you're welcome to sign up using my referral link below:

${referralLink}

There's absolutely no obligation. I simply wanted to pass on an opportunity that many people find rewarding and worthwhile.`;

    if (navigator.share) {
      navigator
        .share({
          title: "Join TopCoin UK - The Next Generation Loyalty Platform",
          text: shareMessage,
        })
        .catch((err) => {
          console.error("Error sharing:", err);
          // Fallback to clipboard if share fails
          navigator.clipboard
            .writeText(shareMessage)
            .then(() =>
              alert(
                "Share message copied to clipboard! You can now paste it in any messaging app."
              )
            )
            .catch((err) => console.error("Failed to copy:", err));
        });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard
        .writeText(shareMessage)
        .then(() =>
          alert(
            "Share message copied to clipboard! You can now paste it in any messaging app."
          )
        )
        .catch((err) => {
          console.error("Failed to copy:", err);
          // Ultimate fallback - show the message in an alert
          alert(`Share this message:\n\n${shareMessage}`);
        });
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ marginTop: "20px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ color: "red" }}>Error: {error}</div>
      </div>
    );
  }

  if (!referralData) {
    return null;
  }

  const totalEarning = (
    (walletData?.points || 0) * 0.005 +
    (walletData?.cash_balance || 0) +
    (agentWalletData?.points || 0) * 0.005
  ).toFixed(2);

  // Prepare status items for StatusGrid
  const statusItems = [];

  // First card - Customers Referred
  statusItems.push({
    img: building,
    value: referralData.counts.customersReferred,
    label: "Customers Referred",
    bgColor: "#f0fdf4",
    bgColor1: "#dcfce7",
  });

  // Third card - Total Earnings
  statusItems.push({
    img: dollarY,
    value: `£ ${totalEarning}`,
    label: "Total Earnings",
    bgColor: "#fffbeb",
    bgColor1: "#fef3c7",
  });

  return (
    <div style={containerStyle}>
      <div className="refer-container1">
        <HeaderCard
          logo={logo}
          title="TopCoin UK"
          subtitle="CREATE CUSTOMER INVITATION LINK"
          description="Refer customers and earn TopCoin as they make purchases."
        />

        <ReferralLinkCard
          link={referralData.user.referralLink}
          onCopy={handleCopyLink}
          onShare={handleShareLink}
        />

        <StatusGrid items={statusItems} />
      </div>
    </div>
  );
};

export default ReferCustomer;
